// PuttyTerminal.js
// Terminal SSH interactif avec xterm.js et WebSocket

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { Terminal } from 'xterm';
import 'xterm/css/xterm.css';

export default function PuttyTerminal({ open, onClose }) {
  const xtermRef = useRef(null);
  const termRef = useRef(null);
  const wsRef = useRef(null);
  const [connecting, setConnecting] = useState(false);
  const [form, setForm] = useState({ host: '', port: 22, user: '', password: '' });
  const [error, setError] = useState('');
  
  // Affiche le terminal dans la boîte de dialogue
  useEffect(() => {
    if (open && xtermRef.current && !termRef.current) {
      termRef.current = new Terminal({
        fontFamily: 'monospace',
        fontSize: 15,
        theme: { background: '#222', foreground: '#0f0' },
        cursorBlink: true,
        cols: 80,
        rows: 24,
        disableStdin: false,
        allowTransparency: true,
      });
      termRef.current.open(xtermRef.current);
      termRef.current.focus();
    }
    return () => {
      if (termRef.current) {
        termRef.current.dispose();
        termRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [open]);

  // Connexion WebSocket SSH
  const handleConnect = () => {
    setConnecting(true);
    setError('');
    if (termRef.current) termRef.current.clear();
    const ws = new window.WebSocket(`ws://${window.location.hostname}:3000/ws/ssh`);
    wsRef.current = ws;
    ws.onopen = () => {
      // Envoie les infos de connexion SSH
      ws.send(JSON.stringify({
        action: 'connect',
        ...form
      }));
    };
    ws.onmessage = (event) => {
      if (!termRef.current) return;
      // Gestion des messages SSH
      let msg;
      try { msg = JSON.parse(event.data); } catch { msg = event.data; }
      if (msg.type === 'error') {
        setError(msg.message);
        ws.close();
        setConnecting(false);
      } else if (msg.type === 'ready') {
        setConnecting(false);
        termRef.current.writeln('\x1b[32m[Connecté à SSH]\x1b[0m');
      } else if (msg.type === 'data') {
        termRef.current.write(msg.data);
      } else if (typeof msg === 'string') {
        termRef.current.write(msg);
      }
    };
    ws.onclose = () => {
      if (termRef.current) termRef.current.writeln('\r\n\x1b[31m[Déconnecté]\x1b[0m');
      setConnecting(false);
    };
    ws.onerror = () => {
      setError('Erreur de connexion WebSocket');
      setConnecting(false);
    };
    // Relayer l'entrée clavier vers le SSH
    if (termRef.current) {
      termRef.current.onData(data => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ action: 'input', data }));
      });
    }
  };

  const handleClose = () => {
    if (wsRef.current) wsRef.current.close();
    if (termRef.current) termRef.current.clear();
    setForm({ host: '', port: 22, user: '', password: '' });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Connexion SSH interactive</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label="Hôte" value={form.host} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} size="small" sx={{ flex: 1 }} />
          <TextField label="Port" type="number" value={form.port} onChange={e => setForm(f => ({ ...f, port: e.target.value }))} size="small" sx={{ width: 100 }} />
          <TextField label="Utilisateur" value={form.user} onChange={e => setForm(f => ({ ...f, user: e.target.value }))} size="small" sx={{ flex: 1 }} />
          <TextField label="Mot de passe" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} size="small" sx={{ flex: 1 }} />
          <Button variant="contained" color="success" onClick={handleConnect} disabled={connecting || !form.host || !form.user || !form.password}>
            Connexion
          </Button>
        </Box>
        {error && <Typography color="error.main" sx={{ mb: 1 }}>{error}</Typography>}
        {connecting && <Box sx={{ mb: 1 }}><CircularProgress size={24} /></Box>}
        <Box ref={xtermRef} sx={{ width: '100%', height: 400, background: '#222', borderRadius: 1, overflow: 'hidden' }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}
